// Where an X account runs and what it is doing, in plain English. Pure (no React) so the
// switcher, Settings → X accounts and Home all agree, and so it is unit-tested.
//
// Inputs come from IgAccount:
//   engineStatus — running / paused (X pushed back) as last reported by the extension
//   driver       — the one browser (extension installation) that runs the account; `active` while
//                  it heartbeats within IG_DRIVER_STALE_MS
//   lastSeenAt   — fallback for extensions ≤ 0.1.4 that report the account without driver bookkeeping

import { IG_DRIVER_STALE_MS, type EngineStatus, type IgAccountDriver, type Millis } from "@gmx/shared";
import { formatDateTime, relativeTime } from "./format";

export type AccountRunState = "running" | "paused" | "standby" | "no_browser";

export interface RunStatusInput {
  engineStatus: EngineStatus | null | undefined;
  lastSeenAt: Millis | null;
  /** Optional so older API responses without the field still work. */
  driver?: IgAccountDriver | null;
}

/** The browser running this account right now, or null. */
export function activeDriver(a: RunStatusInput, now = Date.now()): IgAccountDriver | null {
  const d = a.driver ?? null;
  if (!d || !d.active) return null;
  // The flag is computed when the API answered; don't keep trusting it from a stale cache.
  if (d.seenAt !== null && now - d.seenAt > IG_DRIVER_STALE_MS) return null;
  return d;
}

/** Is any browser running this account? Falls back to lastSeenAt when no driver is recorded (older extensions). */
export function hasActiveBrowser(a: RunStatusInput, now = Date.now()): boolean {
  if (a.driver) return activeDriver(a, now) !== null;
  return a.lastSeenAt !== null && now - a.lastSeenAt <= IG_DRIVER_STALE_MS;
}

/**
 * One state for the dot:
 *   paused     — X pushed back; nothing runs until pausedUntil (shown even with no browser: it's a safety fact)
 *   no_browser — no browser is running the account
 *   running    — a browser runs it and automations are working
 *   standby    — a browser runs it, nothing to do right now (automation off, outside active hours, idle)
 */
export function accountRunState(a: RunStatusInput, now = Date.now()): AccountRunState {
  const e = a.engineStatus;
  if (e?.pausedUntil && e.pausedUntil > now) return "paused";
  if (!hasActiveBrowser(a, now)) return "no_browser";
  return e?.running ? "running" : "standby";
}

export const RUN_STATE_LABELS: Record<AccountRunState, string> = {
  running: "Running",
  paused: "Paused",
  standby: "Standing by",
  no_browser: "Not running in any browser",
};

/** Badge tone per state. */
export const RUN_STATE_TONE: Record<AccountRunState, "success" | "warn" | "info" | "neutral"> = {
  running: "success",
  paused: "warn",
  standby: "info",
  no_browser: "neutral",
};

/** "Running", "Paused until today at 3:40 PM", "Standing by", "Not running in any browser". */
export function runStateLabel(a: RunStatusInput, now = Date.now()): string {
  const st = accountRunState(a, now);
  if (st === "paused") return `Paused until ${formatDateTime(a.engineStatus?.pausedUntil)}`;
  return RUN_STATE_LABELS[st];
}

/** A short "who runs it" for tight spots (the switcher): the browser label, or null. */
export function runsInLabel(a: RunStatusInput, now = Date.now()): string | null {
  const d = activeDriver(a, now);
  if (d) return d.label.trim() || "a browser";
  return !a.driver && hasActiveBrowser(a, now) ? "a browser" : null;
}

/** The compact line for the switcher: "Running · Work profile", "Paused until …", "No browser running it". */
export function shortRunLine(a: RunStatusInput, now = Date.now()): string {
  const st = accountRunState(a, now);
  if (st === "no_browser") return "No browser running it";
  const head = st === "paused" ? `Paused until ${formatDateTime(a.engineStatus?.pausedUntil)}` : RUN_STATE_LABELS[st];
  const where = runsInLabel(a, now);
  return where ? `${head} · ${where}` : head;
}

/**
 * The full line for account cards:
 *   "Runs in: Work profile · extension v0.2.0 · seen 2 min ago"
 *   "No browser is running this account · last ran in Work profile 3 hours ago"
 *   "No browser is running this account"
 */
export function runsInLine(a: RunStatusInput, now = Date.now()): string {
  const d = activeDriver(a, now);
  if (d) {
    const parts = [`Runs in: ${d.label.trim() || "a browser"}`];
    if (d.extensionVersion) parts.push(`extension v${d.extensionVersion}`);
    if (d.seenAt) parts.push(`seen ${relativeTime(d.seenAt, now)}`);
    return parts.join(" · ");
  }
  if (!a.driver && hasActiveBrowser(a, now)) {
    // An older extension reports the account but not which browser it is.
    return `Runs in: a browser with an older Grow My X extension · seen ${relativeTime(a.lastSeenAt, now)}`;
  }
  const last = a.driver;
  if (last?.seenAt) return `No browser is running this account · last ran in ${last.label.trim() || "a browser"} ${relativeTime(last.seenAt, now)}`;
  return "No browser is running this account";
}
